import React, { useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userListsAtom } from '../../store/portfolio';
import { IUserListRef } from '../../types/company';

interface ScopeSelectorProps {
  portfolioEnabled: boolean;
  allListsEnabled: boolean;
  selectedLists: number[];
  onPortfolioChange: (checked: boolean) => void;
  onAllListsChange: (checked: boolean) => void;
  onListsChange: (listId: number, checked: boolean, allListsSelected: boolean) => void;
  sectionId: string;
}
const ScopeSelector: React.FC<ScopeSelectorProps> = ({
  portfolioEnabled,
  allListsEnabled,
  selectedLists,
  onPortfolioChange,
  onAllListsChange,
  onListsChange,
  sectionId,
}) => {
  const userLists = useRecoilValue<IUserListRef[]>(userListsAtom);
  const listIds = userLists.map((list) => list.id);

  const effectiveAllLists = useMemo(() => allListsEnabled && !portfolioEnabled, [allListsEnabled, portfolioEnabled]);

  // Only allow collapsing when we have > 3 lists
  const isCollapsible = listIds.length > 3;

  // Collapse by default when there are more than 3 lists
  const defaultExpanded = useMemo(() => {
    return !isCollapsible;
  }, [isCollapsible]);

  const [listsExpanded, setListsExpanded] = useState<boolean>(defaultExpanded);

  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const listsToggleId = `lists-${sectionId}-toggle`;
  const listsPanelId = `lists-${sectionId}-panel`;

  const listsSummary = useMemo(() => {
    const total = listIds.length;
    if (total === 0) return '0';
    if (effectiveAllLists) return `All lists selected (${total})`;
    return `${selectedLists.length} selected out of ${total}`;
  }, [listIds.length, effectiveAllLists, selectedLists.length]);

  const collapseLists = () => {
    if (!isCollapsible) return;

    const active = document.activeElement as HTMLElement | null;
    const focusInsidePanel = !!(active && panelRef.current && panelRef.current.contains(active));

    setListsExpanded(false);

    if (focusInsidePanel) {
      window.requestAnimationFrame(() => toggleButtonRef.current?.focus());
    }
  };

  const toggleLists = () => {
    if (!isCollapsible) return;
    setListsExpanded((v) => !v);
  };

  const handleToggleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!isCollapsible) return;

    // Ensure consistent behavior
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleLists();
    }
  };

  const handlePanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isCollapsible) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      collapseLists();
    }
  };

  const handlePortfolioChange = (checked: boolean) => {
    if (!checked && !allListsEnabled && selectedLists.length === 0) {
      return; // Prevent unchecking if no other selections exist
    }
    onPortfolioChange(checked);
  };

  const handleAllListsChange = (checked: boolean) => {
    if (!checked && !portfolioEnabled && selectedLists.length === 0) {
      return; // Prevent unchecking if no other selections exist
    }
    onAllListsChange(checked);
  };

  const handleIndividualListChange = (listId: number, checked: boolean) => {
    if (effectiveAllLists) {
      onAllListsChange(false);
      onListsChange(listId, true, false);
      return;
    }

    // prevent unchecking the last individual list if no other selections exist
    if (!checked && !portfolioEnabled && !allListsEnabled && selectedLists.length === 1 && selectedLists.includes(listId)) {
      return;
    }

    const newSelectedLists = checked ? [...selectedLists, listId] : selectedLists.filter((id) => id !== listId);
    const allListsSelected = newSelectedLists.length === listIds.length;

    onListsChange(listId, checked, allListsSelected);
  };

  const listItemFadedClass = effectiveAllLists ? 'opacity-50' : '';

  const showDisclosureHeader = isCollapsible;

  const panelExpanded = isCollapsible ? listsExpanded : true;
  const shouldEnableListScroll = listIds.length > 5;

  return (
    <div className="flex flex-col gap-2">
      {/* portfolio always visible */}
      <div className="flex gap-3 items-start py-2 items-center">
        <input
          type="checkbox"
          checked={portfolioEnabled}
          onChange={(e) => handlePortfolioChange(e.target.checked)}
          className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
          id={`portfolio-${sectionId}-checkbox`}
        />
        <label htmlFor={`portfolio-${sectionId}-checkbox`} className="font-medium text-base text-black cursor-pointer self-center">
          Portfolio
        </label>
      </div>

      {listIds.length === 0 ? (
        <p>
          To configure list alerts, <a href="/">add companies to lists</a>.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {/* lists disclosure header (only when collapsible) */}
          {showDisclosureHeader ? (
            <button
              ref={toggleButtonRef}
              id={listsToggleId}
              type="button"
              aria-expanded={panelExpanded}
              aria-controls={listsPanelId}
              onClick={toggleLists}
              onKeyDown={handleToggleKeyDown}
              className={[
                'w-full',
                'flex items-center justify-between gap-3',
                'py-2',
                'text-left',
                'rounded',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ow-blue',
              ].join(' ')}
            >
              <span className="flex items-center gap-2 min-w-0">
                {/* caret */}
                <span
                  aria-hidden="true"
                  className={[
                    'inline-flex',
                    'transition-transform duration-200 ease-out',
                    panelExpanded ? 'rotate-90' : 'rotate-0',
                    'text-gray-900',
                  ].join(' ')}
                >
                  {' \u25B6 '}
                </span>

                <span className="font-medium text-base text-black">Lists</span>
              </span>

              {/* Summary */}
              {!portfolioEnabled && <span className="text-sm text-gray-900 whitespace-nowrap">{listsSummary}</span>}
            </button>
          ) : (
            // con-collapsible case: keep the same "Lists" label style (no disclosure button)
            <div className="flex items-center justify-between py-2">
              <span className="font-medium text-base text-black">Lists</span>
              {!portfolioEnabled && <span className="text-sm text-gray-900 whitespace-nowrap">{listsSummary}</span>}
            </div>
          )}

          {/* collapsible panel */}
          <div
            ref={panelRef}
            id={listsPanelId}
            role="region"
            aria-labelledby={showDisclosureHeader ? listsToggleId : undefined}
            onKeyDown={handlePanelKeyDown}
            aria-hidden={showDisclosureHeader ? !panelExpanded : undefined}
            className={[
              showDisclosureHeader ? 'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out' : '',
              showDisclosureHeader ? (panelExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0') : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={showDisclosureHeader ? 'overflow-hidden' : undefined}>
              <div className={shouldEnableListScroll ? 'max-h-[300px] overflow-y-auto pr-1 ow-scrollbar' : undefined}>
                <div className="flex flex-col gap-2">
                  {/* all Lists */}
                  <div className="flex gap-3 items-start py-2 items-center">
                    <input
                      type="checkbox"
                      checked={effectiveAllLists}
                      onChange={(e) => handleAllListsChange(e.target.checked)}
                      className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
                      id={`all-lists-${sectionId}-checkbox`}
                      tabIndex={panelExpanded ? 0 : -1}
                    />
                    <label
                      htmlFor={`all-lists-${sectionId}-checkbox`}
                      className="font-medium text-base text-black cursor-pointer self-center"
                    >
                      All Lists
                    </label>
                  </div>

                  {/* individual lists */}
                  {listIds.map((listId) => {
                    const checked = selectedLists.includes(listId) || effectiveAllLists;
                    const listName = userLists.find((list) => list.id === listId)?.name ?? `List ${listId}`;

                    return (
                      <div key={listId} className="flex gap-3 items-start py-2 items-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleIndividualListChange(listId, e.target.checked)}
                          className={`accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer ${listItemFadedClass}`}
                          id={`list-${sectionId}-${listId}`}
                          tabIndex={panelExpanded ? 0 : -1}
                        />
                        <label
                          htmlFor={`list-${sectionId}-${listId}`}
                          className={`font-medium text-base text-black cursor-pointer self-center ${listItemFadedClass}`}
                        >
                          {listName}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopeSelector;
